import React from 'react';

import { arrayMove } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';

import useCollection from 'components/hooks/useCollection';
import useSidePanel from 'components/hooks/useSidePanel';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import Tooltip from 'components/application/Tooltip';

import CustomPersonsSidePanel from 'components/contentRegister/components/customPersons/components/CustomPersonsSidePanel';
import { useContentRegisterContext } from 'components/contentRegister/contexts/ContentRegisterContext';
import { useBreadBoard } from 'components/contexts/Toaster';

export default function CustomPersons() {
  const { defaultPersonsAtRiskOptions, personsAtRiskPolicy } =
    useContentRegisterContext();

  const { addInedibleToast } = useBreadBoard();

  const [
    personsAtRisks,
    loadCollection,
    handleChange,
    handleModify,
    cancelChanges,
    _setCollectionErrors,
    collectionErrors,
    isSubmitDisabled,
    isValidationError,
    setIsFallback,
    resetCollectionErrors,
    removeErrorStyling,
  ] = useCollection([]);

  const [isSidePanelOpen, , openSidePanel, closeSidePanel] =
    useSidePanel(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'contentRegister.components.customPersons.components.CustomPersons',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const handleOpenClick = () => {
    if (!personsAtRisks.loaded) getPersonsAtRisks();

    openSidePanel();
  };

  const handleDelete = (index) => {
    handleModify({ action: 'remove', index });
  };

  const handleAddButtonClick = () => {
    handleModify({ action: 'add', newData: { name: '', position: null } });
  };

  const handleCancelButtonClick = () => {
    closeSidePanel();
    cancelChanges();
  };

  const handleResetDefaults = () => {
    loadCollection(defaultPersonsAtRisks);
    resetCollectionErrors();
  };

  const personsAtRisksSettingsParams = ({ personsAtRisksResponse }) => {
    const existingPersonsAtRisks = [...personsAtRisksResponse.data.data];

    const personsAtRiskAttributes = personsAtRisks.collection.map(
      (member, index) => {
        const existingMember = existingPersonsAtRisks.find(
          (resource) =>
            trimmed(resource.attributes.name) === trimmed(member.name),
        );
        existingMember &&
          existingPersonsAtRisks.splice(
            existingPersonsAtRisks.indexOf(existingMember),
            1,
          );

        return {
          id: existingMember?.id || null,
          name: trimmed(member.name),
          position: index + 1,
        };
      },
    );

    existingPersonsAtRisks.forEach((resource) => {
      personsAtRiskAttributes.push({ id: resource.id, _destroy: '1' });
    });

    return {
      persons_at_risks_settings: {
        persons_at_risks_attributes: personsAtRiskAttributes,
      },
    };
  };

  const trimmed = (word) => word.trim();

  const onSortEnd = ({ newIndex, oldIndex }) => {
    let newList = [...personsAtRisks.collection];
    newList = arrayMove(newList, oldIndex, newIndex);
    loadCollection(newList);
  };

  const isBlank = (member) => (member.name === '' ? 'isBlank' : false);

  const isDuplicate = (member, collection) => {
    const names = collection.map((item) => trimmed(item.name));
    const numOfMatches = names.filter(
      (item) => item === trimmed(member.name),
    ).length;

    return numOfMatches > 1 ? 'isDuplicate' : false;
  };

  const displayValidationErrors = collectionErrors.errorList.reduce(
    (acc, item) => {
      if (item) {
        acc[item.errorMessage] = {
          error: { detail: i18nT(`errorMessages.${item.errorMessage}`) },
        };
      }

      return acc;
    },
    {},
  );

  const getPersonsAtRisks = () => {
    axios
      .get('/persons_at_risks')
      .then((response) => {
        if (response.data.data.length > 0) {
          const newCollection = response.data.data.map(
            (resource) => resource.attributes,
          );

          loadCollection(newCollection);
        } else {
          loadCollection(defaultPersonsAtRisks);
        }
      })
      .catch(addInedibleToast);
  };

  const updatePersonsAtRisks = () => {
    axios.get('/persons_at_risks').then((getResponse) => {
      axios
        .patch(
          '/persons_at_risks_settings',
          personsAtRisksSettingsParams({ personsAtRisksResponse: getResponse }),
        )
        .then((patchResponse) => {
          const newCollection = patchResponse.data.data.map(
            (resource) => resource.attributes,
          );

          loadCollection(newCollection);
          resetCollectionErrors();
          closeSidePanel();
        })
        .catch(() => {
          if (!isValidationError([isBlank, isDuplicate])) {
            setIsFallback(true);
          }
        });
    });
  };

  const defaultPersonsAtRisks = defaultPersonsAtRiskOptions.map(
    (option, index) => ({ name: option, position: index + 1 }),
  );

  return (
    <>
      <div className='tooltip-parent'>
        <OutlinedButton
          color='grey'
          disabled={!personsAtRiskPolicy.canEdit}
          onClick={handleOpenClick}
          size='sm'
        >
          {i18nTCommon('riskRegister.editPersonsAtRisk')}
        </OutlinedButton>
        {!personsAtRiskPolicy.canEdit && (
          <Tooltip
            placement='top'
            tooltip={i18nT('disabledTooltip.notEditable')}
            trigger='hover'
          />
        )}
      </div>
      <CustomPersonsSidePanel
        closeSidePanel={closeSidePanel}
        collection={personsAtRisks.collection}
        collectionErrors={collectionErrors}
        displayValidationErrors={displayValidationErrors}
        isFallback={collectionErrors.isFallback}
        isSidePanelOpen={isSidePanelOpen}
        isSubmitDisabled={isSubmitDisabled}
        onAdd={handleAddButtonClick}
        onCancel={handleCancelButtonClick}
        onDelete={handleDelete}
        onListInputChange={handleChange}
        onSave={updatePersonsAtRisks}
        onSortEnd={onSortEnd}
        removeErrorStyling={removeErrorStyling}
        resetDefaults={handleResetDefaults}
      />
    </>
  );
}
